import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Divider,
  createTheme,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import {
  Search,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Inventory2 as InventoryIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { FaX, FaCheck, FaFileCirclePlus, FaFilePen, FaPrint } from "react-icons/fa6";
import { AddCircleOutline } from "@mui/icons-material";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../login/AuthContext";
import axios from "axios";
import config from "../../config/config";
import IconActionsRenderer from "../../components/IconActionsRenderer.jsx";
import { toast } from "react-toastify";

const ONAYLA_FIELD_LABELS = {
  Unvan: "Firma Adı (Unvan)",
  VergiDairesi: "Vergi Dairesi",
  VergiNo: "Vergi No",
  TCKimlikNo: "TC Kimlik No",
  Yetkili: "Yetkili",
  EPosta: "E-Posta",
  Tel: "Tel",
  Tel2: "Tel 2",
  Adres: "Adres",
  Il: "İl",
  Ilce: "İlçe",
};

const isEmptyCariValue = (v) => {
  if (v == null) return true;
  const s = String(v).trim();
  return s === "" || s === "-";
};

const getOnaylaCariEksikler = (c) => {
  const eksik = [];
  ["Unvan", "VergiDairesi", "Yetkili", "EPosta", "Tel", "Adres", "Il", "Ilce"].forEach((key) => {
    if (isEmptyCariValue(c?.[key])) eksik.push(ONAYLA_FIELD_LABELS[key]);
  });
  if (isEmptyCariValue(c?.VergiNo) && isEmptyCariValue(c?.TCKimlikNo)) {
    eksik.push("Vergi No veya TC Kimlik No");
  }
  return eksik;
};

const isOnaylaCariComplete = (c) => getOnaylaCariEksikler(c).length === 0;

const formatOnaylaFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getOnaylaFieldError = (field, customer) => {
  if (field === "VergiNo" || field === "TCKimlikNo") {
    if (isEmptyCariValue(customer?.VergiNo) && isEmptyCariValue(customer?.TCKimlikNo)) {
      return "Vergi No veya TC Kimlik No'dan en az biri zorunludur";
    }
    return "";
  }
  if (isEmptyCariValue(customer?.[field])) return "Bu alan zorunludur";
  return "";
};

const normalizeOnaylaFieldValue = (field, value) => {
  if (field === "VergiNo") return String(value ?? "").replace(/\D/g, "").slice(0, 10);
  if (field === "TCKimlikNo") return String(value ?? "").replace(/\D/g, "").slice(0, 11);
  return value ?? "";
};

const ONAYLA_FORM_FIELDS = [
  { field: "Unvan", label: "Firma Adı (Unvan)", sm: 6, required: true },
  { field: "VergiDairesi", label: "Vergi Dairesi", sm: 6, required: true },
  { field: "VergiNo", label: "Vergi No", sm: 6, required: false, numeric: 10 },
  { field: "TCKimlikNo", label: "TC Kimlik No", sm: 6, required: false, numeric: 11 },
  { field: "Yetkili", label: "Yetkili", sm: 6, required: true },
  { field: "EPosta", label: "E-Posta", sm: 6, required: true },
  { field: "Tel", label: "Tel", sm: 6, required: true },
  { field: "Tel2", label: "Tel 2", sm: 6, required: false },
  { field: "Adres", label: "Adres", sm: 12, required: true },
  { field: "Il", label: "İl", sm: 6, required: true },
  { field: "Ilce", label: "İlçe", sm: 6, required: true },
];

const Offer = () => {
  const { token, hasPermission, userData } = useAuth();
  const outerTheme = useTheme();

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
    });

  // Güvenli tarih oluşturma fonksiyonu
  const getFormattedDate = (daysOffset = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysOffset);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dataTableRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dataFetchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [formValues, setFormValues] = useState({
    MusteriId: "",
    DurumId: "",
    Aciklama: "",
    KorumaBaslangic: getFormattedDate(0),
    KorumaBitis: getFormattedDate(60),
    Etkin: true,
  });
  const [customers, setCustomers] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [customerSearchMessage, setCustomerSearchMessage] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewOffer, setViewOffer] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [aktifPasif, setAktifPasif] = useState("aktif");
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  const customQueryParams = useMemo(() => ({ aktifPasif }), [aktifPasif]);

  // Aktif / Pasifler seçimi değişince tabloyu yenile (silinenler listesi veya aktif listesi)
  useEffect(() => {
    if (!dataTableRef.current?.refreshData) return;
    const t = setTimeout(() => dataTableRef.current.refreshData(), 0);
    return () => clearTimeout(t);
  }, [aktifPasif]);

  const [printBodyHtml, setPrintBodyHtml] = useState(null);
  const [printCssText, setPrintCssText] = useState("");
  const [printFullHtml, setPrintFullHtml] = useState(null); // format=html tam doküman – iframe ile yazdır
  const [printLoading, setPrintLoading] = useState(false);
  const printIframeRef = useRef(null);
  const [onaylaModalOpen, setOnaylaModalOpen] = useState(false);
  const [onaylaCustomer, setOnaylaCustomer] = useState({});
  const [onaylaFiles, setOnaylaFiles] = useState([]);
  const [onaylaLoading, setOnaylaLoading] = useState(false);
  const [onaylaMissingFields, setOnaylaMissingFields] = useState([]);
  const [onaylaAttempted, setOnaylaAttempted] = useState(false);
  const onaylaFileInputRef = useRef(null);
  const [onaylaFileDragOver, setOnaylaFileDragOver] = useState(false);

  const addOnaylaPdfFiles = useCallback((fileList) => {
    const pdfs = Array.from(fileList || []).filter(
      (f) => f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf")
    );
    if (pdfs.length === 0 && fileList?.length > 0) {
      toast.error("Yalnızca PDF dosyası yükleyebilirsiniz.");
      return;
    }
    if (pdfs.length > 0) setOnaylaFiles((prev) => [...prev, ...pdfs]);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setIsLoading(value.length >= 3);
  }, []);

  const axiosInstance = useCallback(() => {
    return axios.create({
      baseURL: config.baseURL,
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  const handleDeleteOffer = useCallback(
    async (id) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.delete(`/teklif-koruma/${id}`, { params: { aktifPasif } });
    },
    [token, aktifPasif]
  );

  // Fetch status options from database
  const fetchStatusOptions = useCallback(async () => {
    try {
      setIsStatusLoading(true);
      const api = axiosInstance();
      const res = await api.get("/teklif-koruma/durumlar");
      setStatusOptions(res.data || []);
    } catch (error) {
      console.error("Durumlar yüklenirken hata:", error);
      toast.error("Durumlar yüklenemedi");
    } finally {
      setIsStatusLoading(false);
    }
  }, [axiosInstance]);

  // Görüntüle: sadece teklif koruma için; her bayi kendi teklifini, Bayi 1 hepsini görür (backend 403 döner yetkisizse)
  const handleViewOffer = useCallback(
    async (offerId) => {
      setViewLoading(true);
      setViewOffer(null);
      try {
        const api = axiosInstance();
        const res = await api.get(`/teklif-koruma/${offerId}`);
        setViewOffer(res?.data ?? null);
        setViewModalOpen(true);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error(e?.response?.data?.message || "Bu teklif için yetkiniz yoktur.");
        } else {
          toast.error("Teklif detayları alınamadı");
        }
      } finally {
        setViewLoading(false);
      }
    },
    [axiosInstance]
  );

  // Edit action from DataTable -> open drawer prefilled
  const handleEditOffer = useCallback(
    async (offerId) => {
      try {
        const api = axiosInstance();
        const res = await api.get(`/teklif-koruma/${offerId}`);
        const offer = res?.data;
        setEditingOfferId(offerId);
        setFormValues({
          MusteriId: offer?.MusteriId || "",
          DurumId: offer?.DurumId || "",
          Aciklama: offer?.Aciklama || "",
          KorumaBaslangic: offer?.KorumaBaslangic
            ? String(offer.KorumaBaslangic).substring(0, 10)
            : "",
          KorumaBitis: offer?.KorumaBitis
            ? String(offer.KorumaBitis).substring(0, 10)
            : "",
          Etkin: !!offer?.Etkin,
        });
        // Prefill selected customer
        if (offer?.MusteriId) {
          try {
            const custRes = await api.get(`/musteriler/${offer.MusteriId}`);
            const custData = custRes?.data?.data || custRes?.data;
            setSelectedCustomer(custData || null);
          } catch {}
        }
        setIsDrawerOpen(true);
      } catch (e) {
        toast.error("Teklif detayları alınamadı");
      }
    },
    [axiosInstance]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (dataFetchTimeoutRef.current) {
      clearTimeout(dataFetchTimeoutRef.current);
    }

    dataFetchTimeoutRef.current = setTimeout(() => {
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);

    return () => {
      if (dataFetchTimeoutRef.current)
        clearTimeout(dataFetchTimeoutRef.current);
    };
  }, [searchText]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (dataFetchTimeoutRef.current)
        clearTimeout(dataFetchTimeoutRef.current);
    };
  }, []);

  // Fetch status options on component mount
  useEffect(() => {
    fetchStatusOptions();
  }, [fetchStatusOptions]);

  const StatusPillRenderer = useCallback((props) => {
    let value =
      props?.value ||
      props?.data?.Durum?.DurumAdi ||
      props?.data?.DurumAdi ||
      "";
    if (value && String(value).toLowerCase().includes("onaylandı")) value = "Onaylandı";
    if (value && String(value).toLowerCase().includes("red edildi")) value = "Red Edildi";
    const getStatusColor = (status) => {
      switch (status) {
        case "":
          return;
        case "Onay Bekliyor":
        case "Bekliyor":
        case "Onayda Bekliyor":
          return "gray";
        case "Onaylandı":
        case "Sonuçlandı":
        case "Lisans Üretildi":
          return "green";
        case "Red Edildi":
        case "Reddedildi":
          return "#d32f2f";
        default:
          return "orange";
      }
    };
    return (
      <div
        style={{
          backgroundColor: getStatusColor(value),
          color: "white",
          padding: "4px 8px",
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "80%",
          width: "90%",
        }}
      >
        {value}
      </div>
    );
  }, []);

  const teklifColumnDefs = useMemo(
    () => [
      {
        headerName: "İşlemler",
        field: "actions",
        minWidth: 130,
        maxWidth:130,
        sortable: false,
        filter: false,
        cellRenderer: IconActionsRenderer,
        pinned: "left",
        cellClass: "action-cell-center",
      },
      {
        headerName: "Oluşturma Tarihi",
        field: "KayitTarihi",
        valueFormatter: (params) =>
          params.value
            ? new Date(params.value).toLocaleDateString("tr-TR")
            : "",
        sortable: true,
        filter: "agDateColumnFilter",
        minWidth: 130,
      },
      {
        headerName: "Teklif No",
        field: "TeklifNo",
        sortable: true,
        filter: true,
        minWidth: 160,
        filterParams: { buttons: ["sıfırla", "uygula"], closeOnApply: true },
      },
      {
        headerName: "Bayi",
        field: "BayiAdi",
        valueGetter: (params) => {
          // Backend'den gelen BayiAdi veya nested Bayi
          return params.data?.BayiAdi || 
                 params.data?.Bayi?.BayiAdi || 
                 "";
        },
        sortable: true,
        filter: true,
        minWidth: 140,
        filterParams: { buttons: ["sıfırla", "uygula"], closeOnApply: true },
      },
      {
        headerName: "Müşteri",
        field: "MusteriUnvan",
        valueGetter: (params) => {
          // Backend'den gelen MusteriUnvan veya nested Musteri/PotansiyelMusteri
          return params.data?.MusteriUnvan || 
                 params.data?.Musteri?.Unvan || 
                 params.data?.PotansiyelMusteri?.Unvan || 
                 "";
        },
        sortable: true,
        filter: true,
        minWidth: 220,
        filterParams: { buttons: ["sıfırla", "uygula"], closeOnApply: true },
      },
      {
        headerName: "Başlangıç",
        field: "KorumaBaslangic",
        valueFormatter: (params) =>
          params.value
            ? new Date(params.value).toLocaleDateString("tr-TR")
            : "",
        sortable: true,
        filter: "agDateColumnFilter",
        minWidth: 130,
      },
      {
        headerName: "Bitiş",
        field: "KorumaBitis",
        valueFormatter: (params) =>
          params.value
            ? new Date(params.value).toLocaleDateString("tr-TR")
            : "",
        sortable: true,
        filter: "agDateColumnFilter",
        minWidth: 130,
      },

      // {
      //   headerName: "Açıklama",
      //   field: "Aciklama",
      //   sortable: true,
      //   filter: true,
      //   minWidth: 220,
      //   filterParams: { buttons: ["sıfırla", "uygula"], closeOnApply: true },
      // },
      {
        headerName: "Durum",
        field: "DurumAdi",
        valueGetter: (params) => {
          const raw = params.data?.DurumAdi || params.data?.Durum?.DurumAdi || "";
          if (!raw) return "";
          return String(raw).toLowerCase().includes("onaylandı") ? "Onaylandı" : raw;
        },
        sortable: true,
        filter: true,
        minWidth: 140,
        cellRenderer: StatusPillRenderer,
        filterParams: { buttons: ["sıfırla", "uygula"], closeOnApply: true },
      },
    ],
    []
  );



  const customContext = useMemo(
    () => ({
      showRestore: false,
      showView: true,
      showEdit: true,
      showDelete: true,
      showTeklifPreview: false,
      onView: handleViewOffer,
      handleEditItem: handleEditOffer,
      handleOpenDeleteModal: (id) => {
        setSelectedItemId(id);
        setDeleteModalOpen(true);
      },
      hasPermission,
    }),
    [hasPermission, handleEditOffer, handleViewOffer]
  );

  const toggleDrawer = useCallback(() => setIsDrawerOpen((p) => !p), []);
  const openDrawerForAdd = useCallback(() => {
    setEditingOfferId(null);
    setFormValues({
      MusteriId: "",
      DurumId: "",
      Aciklama: "",
      KorumaBaslangic: getFormattedDate(0),
      KorumaBitis: getFormattedDate(60),
      Etkin: true,
    });
    setSelectedCustomer(null);
    setIsDrawerOpen(true);
  }, []);

  // Customer Autocomplete search (min 3 chars) similar to DealerForm
  const debouncedCustomerSearchRef = useRef(null);
  const searchCustomers = useCallback(
    async (query) => {
      if (!query || query.length < 3) {
        setCustomers([]);
        setIsCustomerLoading(false);
        setCustomerSearchMessage("En az 3 karakter giriniz");
        return;
      }
      try {
        setIsCustomerLoading(true);
        setCustomerSearchMessage("");
        const res = await axios.get(`${config.baseURL}/musteriler/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { query, searchFields: ["Unvan", "Yetkili","VergiNo"] },
        });
        const list = res?.data?.data || res?.data || [];
        setCustomers(Array.isArray(list) ? list : []);

      } catch (err) {
        setCustomers([]);
        setCustomerSearchMessage("Arama sırasında hata oluştu");
      } finally {
        setIsCustomerLoading(false);
      }
    },
    [token]
  );

  // Dialog tabanlı müşteri arama (min 3 karakter)
  useEffect(() => {
    let active = true;
    const fetchDialogCustomers = async () => {
      if (!searchQuery || searchQuery.length < 3) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const api = axiosInstance();
        const res = await api.get("/musteriler/search", {
          params: { query: searchQuery, limit: 20, page: 1 },
        });
        const payload = res?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        if (active) setSearchResults(list);
      } catch (e) {
        if (active) setSearchResults([]);
      } finally {
        if (active) setIsSearching(false);
      }
    };
    const t = setTimeout(fetchDialogCustomers, 300);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [searchQuery, axiosInstance]);
  const handleFormChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleStartDateChange = useCallback((e) => {
    const startDate = e.target.value;

    // Tarih değerini kontrol et
    if (!startDate) {
      setFormValues((prev) => ({
        ...prev,
        KorumaBaslangic: startDate,
        KorumaBitis: "",
      }));
      return;
    }

    // Geçerli bir tarih olup olmadığını kontrol et
    const dateObj = new Date(startDate);
    if (isNaN(dateObj.getTime())) {
      // Geçersiz tarih, sadece başlangıç tarihini güncelle
      setFormValues((prev) => ({
        ...prev,
        KorumaBaslangic: startDate,
        KorumaBitis: "",
      }));
      return;
    }

    // Geçerli tarih ise bitiş tarihini hesapla
    const endDate = new Date(dateObj);
    endDate.setDate(endDate.getDate() + 60);

    // Bitiş tarihinin de geçerli olup olmadığını kontrol et
    let endDateString = "";
    if (!isNaN(endDate.getTime())) {
      const year = endDate.getFullYear();
      const month = String(endDate.getMonth() + 1).padStart(2, "0");
      const day = String(endDate.getDate()).padStart(2, "0");
      endDateString = `${year}-${month}-${day}`;
    }

    setFormValues((prev) => ({
      ...prev,
      KorumaBaslangic: startDate,
      KorumaBitis: endDateString,
    }));
  }, []);
  const handleCreateOffer = useCallback(async () => {
    try {
      const api = axiosInstance();
      const payload = {
        ...formValues,
        OlusturanKullanici: userData?.Id || userData?.KullaniciId || undefined,
        BayiId: userData?.BayiKodu,
      };
      let response;
      if (editingOfferId) {
        response = await api.put(`/teklif-koruma/${editingOfferId}`, payload);
        toast.success("Teklif başarıyla güncellendi");
      } else {
        response = await api.post("/teklif-koruma", payload);
        const teklifNo = response?.data?.teklifNo || "Bilinmiyor";
        toast.success(
          `Teklif başarıyla oluşturuldu! \nTeklif No: ${teklifNo}`,
          {
            autoClose: 5000,
          }
        );
      }
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
      setIsDrawerOpen(false);
      setFormValues({
        MusteriId: "",
        DurumId: "",
        Aciklama: "",
        KorumaBaslangic: getFormattedDate(0),
        KorumaBitis: getFormattedDate(60),
        Etkin: true,
      });
    } catch (error) {
      console.error("Teklif oluşturma hatası:", error);
      toast.error(error?.response?.data?.message || "Teklif oluşturulamadı");
    }
  }, [axiosInstance, formValues, userData, editingOfferId]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedItemId(null);
  }, []);

  const teklifDataToHtml = useCallback((xml, xsl, logoDataUri) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "text/xml");
    const xslDoc = parser.parseFromString(xsl, "text/xml");
    if (xmlDoc.querySelector("parsererror") || xslDoc.querySelector("parsererror")) {
      throw new Error("XML veya XSL parse hatası");
    }
    const processor = new XSLTProcessor();
    processor.importStylesheet(xslDoc);
    const resultDoc = processor.transformToDocument(xmlDoc);
    const serializer = new XMLSerializer();
    let html = resultDoc?.documentElement ? serializer.serializeToString(resultDoc) : "";
    if (!html && processor.transformToFragment) {
      const frag = processor.transformToFragment(xmlDoc, document);
      if (frag?.childNodes?.length > 0) {
        html = serializer.serializeToString(frag);
        if (!/^\s*<!DOCTYPE/i.test(html) && !/^\s*<html/i.test(html)) {
          html = "<!DOCTYPE html><html><head><meta charset=\"UTF-8\"></head><body>" + html + "</body></html>";
        }
      }
    }
    html = (html || "").replace(/src="[^"]*logo32\.png[^"]*"/gi, 'src="' + logoDataUri + '"');
    html = html.replace(/src='[^']*logo32\.png[^']*'/gi, "src='" + logoDataUri + "'");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (origin) {
      html = html.replace(/src="(\/\d+_logo\.png)"/gi, 'src="' + origin + '$1"');
      html = html.replace(/src='(\/\d+_logo\.png)'/gi, "src='" + origin + "$1'");
    }
    if (!/^\s*<!DOCTYPE\s+html/i.test(html)) html = "<!DOCTYPE html>\n" + html;
    return html;
  }, []);

  const splitHtmlForPrint = useCallback((fullHtml) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(fullHtml || ""), "text/html");
    const body = doc?.body ? doc.body.innerHTML : String(fullHtml || "");
    const css = Array.from(doc.querySelectorAll("style")).map((s) => s.textContent || "").filter(Boolean).join("\n");
    return { bodyHtml: body, cssText: css };
  }, []);

  // Yazdır: Sunucudan xml + xsl al, XSLT ile HTML üret (teklif.xslt ile aynı dizayn), iframe'de yazdır
  const handlePrintOffer = useCallback(
    async (offerId) => {
      if (!offerId) return;
      setPrintLoading(true);
      try {
        const api = axiosInstance();
        const res = await api.get(`/teklif-koruma/belge/${offerId}`);
        const data = res?.data?.data ?? res?.data ?? {};
        const xml = data.xml;
        const xsl = data.xsl;
        const logoDataUri = data.logoDataUri || "";
        if (!xml || !xsl || typeof xml !== "string" || typeof xsl !== "string") {
          toast.error("Teklif belgesi alınamadı veya içerik boş");
          return;
        }
        const html = teklifDataToHtml(xml, xsl, logoDataUri);
        if (!html || typeof html !== "string" || html.length < 100) {
          toast.error("Teklif belgesi alınamadı veya içerik boş");
          return;
        }
        setPrintFullHtml(html);
        toast.success("Yazdırma penceresi açılıyor.");
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error(e?.response?.data?.message || "Bu teklifi yazdırmak için yetkiniz yoktur.");
        } else {
          toast.error(e?.response?.data?.message || "Teklif belgesi alınamadı");
        }
      } finally {
        setPrintLoading(false);
      }
    },
    [axiosInstance, teklifDataToHtml]
  );

  // Tam HTML iframe ile yazdır (format=html) – içerik aynen gelir
  const handleOpenOnayla = useCallback(() => {
    if (!viewOffer) return;
    const c = viewOffer.Musteri || viewOffer.PotansiyelMusteri;
    if (c) {
      setOnaylaCustomer({
        Unvan: c.Unvan ?? "",
        VergiDairesi: c.VergiDairesi ?? "",
        VergiNo: normalizeOnaylaFieldValue("VergiNo", c.VergiNo),
        TCKimlikNo: normalizeOnaylaFieldValue("TCKimlikNo", c.TCKimlikNo),
        Yetkili: c.Yetkili ?? "",
        EPosta: c.EPosta ?? "",
        Tel: c.Tel ?? "",
        Tel2: c.Tel2 ?? "",
        Adres: c.Adres ?? "",
        Il: c.Il ?? "",
        Ilce: c.Ilce ?? "",
      });
    } else {
      setOnaylaCustomer({});
    }
    setOnaylaFiles([]);
    setOnaylaMissingFields([]);
    setOnaylaAttempted(false);
    setOnaylaModalOpen(true);
  }, [viewOffer]);

  const handleOnaylaSubmit = useCallback(async () => {
    if (!viewOffer?.Id) return;

    const eksik = getOnaylaCariEksikler(onaylaCustomer);
    if (onaylaFiles.length === 0) {
      toast.error("Siparişe dönüştürmek için en az bir PDF dosyası yükleyin.");
      return;
    }
    if (eksik.length > 0) {
      setOnaylaMissingFields(eksik);
      setOnaylaAttempted(true);
      toast.error("Tüm zorunlu alanları doldurun. Vergi No veya TC Kimlik No'dan en az biri girilmelidir.");
      return;
    }

    setOnaylaLoading(true);
    try {
      const customerPayload = {
        ...onaylaCustomer,
        VergiNo: normalizeOnaylaFieldValue("VergiNo", onaylaCustomer.VergiNo),
        TCKimlikNo: normalizeOnaylaFieldValue("TCKimlikNo", onaylaCustomer.TCKimlikNo),
      };
      const formData = new FormData();
      formData.append("customerUpdates", JSON.stringify(customerPayload));
      onaylaFiles.forEach((f) => {
        if (f instanceof File) formData.append("customerFiles", f);
      });
      const api = axiosInstance();
      const res = await api.post(`/teklif-koruma/${viewOffer.Id}/onayla`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success) {
        toast.success(res.data?.message ?? "Teklif siparişe dönüştürüldü.");
        setOnaylaModalOpen(false);
        setViewModalOpen(false);
        setViewOffer(null);
        if (dataTableRef.current?.refreshData) dataTableRef.current.refreshData();
      }
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 400 && Array.isArray(data?.missingFields)) {
        setOnaylaMissingFields(data.missingFields);
        toast.error(data?.message ?? "Eksik bilgileri tamamlayınız.");
      } else {
        toast.error(data?.message ?? "Onaylama sırasında hata oluştu.");
      }
    } finally {
      setOnaylaLoading(false);
    }
  }, [viewOffer, onaylaCustomer, onaylaFiles, axiosInstance]);

  const handlePrintIframeLoad = useCallback(() => {
    const iframe = printIframeRef.current;
    if (!iframe?.contentWindow) return;
    const win = iframe.contentWindow;
    win.addEventListener("afterprint", () => setPrintFullHtml(null));
    // Stiller ve logoların uygulanması için kısa bekleme
    setTimeout(() => win.print(), 400);
  }, []);

  useEffect(() => {
    if (!printBodyHtml) return;
    const onAfterPrint = () => {
      setPrintBodyHtml(null);
      setPrintCssText("");
    };
    window.addEventListener("afterprint", onAfterPrint);
    const triggerPrint = () => {
      window.focus();
      window.print();
    };
    const t = setTimeout(() => {
      const root = document.getElementById("__print_root_offer__");
      if (!root) {
        triggerPrint();
        return;
      }
      const imgs = root.querySelectorAll("img");
      if (imgs.length === 0) {
        triggerPrint();
        return;
      }
      let printed = false;
      const doPrint = () => {
        if (printed) return;
        printed = true;
        triggerPrint();
      };
      let loaded = 0;
      imgs.forEach((img) => {
        if (img.complete) loaded++;
        else {
          img.addEventListener("load", () => { loaded++; if (loaded >= imgs.length) doPrint(); });
          img.addEventListener("error", () => { loaded++; if (loaded >= imgs.length) doPrint(); });
        }
      });
      if (loaded >= imgs.length) doPrint();
      else setTimeout(doPrint, 2500);
    }, 200);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [printBodyHtml]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedItemId) return;

    try {
      await handleDeleteOffer(selectedItemId);
      toast.success("Teklif başarıyla silindi");
      handleCloseDeleteModal();

      // Refresh the table
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    } catch (error) {
      console.error("Teklif silme hatası:", error);
      const msg =
        error.response?.status === 403
          ? (error.response?.data?.message || "Teklifi silmek için yetkiniz yoktur.")
          : (error.response?.data?.error || error.response?.data?.message || "Teklif silinemedi");
      toast.error(msg);
    }
  }, [selectedItemId, handleDeleteOffer, handleCloseDeleteModal]);

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Teklifler</h1>
      </div>

      {isBayiFullAccess && (
        <FormControl component="fieldset" sx={{ mb: 1.5, display: "block" }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
            Liste
          </FormLabel>
          <RadioGroup
            row
            value={aktifPasif}
            onChange={(e) => setAktifPasif(e.target.value)}
            name="aktif-pasif"
          >
            <FormControlLabel value="aktif" control={<Radio size="small" />} label="Aktif" />
            <FormControlLabel value="pasif" control={<Radio size="small" />} label="Pasifler" />
          </RadioGroup>
        </FormControl>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama (en az 3 karakter)"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={openDrawerForAdd}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <div style={{ height: "70vh" }}>
        <DataTable
          ref={dataTableRef}
          searchText={searchText}
          tableType="custom"
          columnDefs={teklifColumnDefs}
          apiEndpoint="/teklif-koruma"
          searchEndpoint="/teklif-koruma"
          onEditAction={handleEditOffer}
          onRowDoubleClicked={(e) => e.data?.Id != null && handleViewOffer(e.data.Id)}
          onDeleteAction={handleDeleteOffer}
          deleteConfirmMessage={aktifPasif === "pasif" ? "Bu teklifi kalıcı olarak silmek istediğinizden emin misiniz?" : "Bu teklifi silmek istediğinizden emin misiniz?"}
          onLoadingChange={setIsLoading}
          customContext={customContext}
          customQueryParams={customQueryParams}
          idField="Id"
        />
      </div>

      <Drawer
        open={isDrawerOpen}
        onClose={toggleDrawer}
        direction="right"
        className="custom-drawer"
        duration={120}
        zIndex={600}
      >
        <ThemeProvider theme={customTheme(outerTheme)}>
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                {editingOfferId ? (
                  <FaFilePen style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                ) : (
                  <FaFileCirclePlus style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                )}
                {editingOfferId ? "Teklif Düzenle" : "Yeni Teklif Ekle"}
              </Typography>
              <Box display="flex" gap={0.5}>
                <Button
                  variant="text"
                  size="medium"
                  onClick={handleCreateOffer}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaCheck style={{ fontSize: "18px" }} />
                </Button>
                <Button
                  variant="text"
                  size="medium"
                  onClick={toggleDrawer}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaX style={{ fontSize: "18px" }} />
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
            {(() => {
              const currentStatus = formValues.DurumId
                ? statusOptions.find(
                    (s) => s.Id === parseInt(formValues.DurumId)
                  )?.DurumAdi
                : "";
              const isEditMode = editingOfferId !== null;
              const fieldsDisabled = isEditMode;

              const SectionHeader = ({ icon: Icon, title }) => (
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Icon
                    sx={{
                      color: 'rgba(0,0,0,0.54)',
                      fontSize: '20px'
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              );

              return (
                <>
                  <Card elevation={0} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <SectionHeader icon={PersonIcon} title="Müşteri Bilgileri" />
                      <Divider sx={{ mb: 2, mt: 0.5 }} />
                      <FormControl fullWidth>
                        <Autocomplete
                          value={selectedCustomer}
                          required
                          onChange={(event, newValue) => {
                            if (!fieldsDisabled) {
                              setSelectedCustomer(newValue);
                              setFormValues((prev) => ({
                                ...prev,
                                MusteriId: newValue?.Id || "",
                              }));
                            }
                          }}
                          onInputChange={(event, newValue) => {
                            if (!fieldsDisabled) {
                              if (!debouncedCustomerSearchRef.current) {
                                debouncedCustomerSearchRef.current = setTimeout(
                                  () => {},
                                  0
                                );
                              }
                              clearTimeout(debouncedCustomerSearchRef.current);
                              debouncedCustomerSearchRef.current = setTimeout(
                                () => searchCustomers(newValue),
                                300
                              );
                            }
                          }}
                          options={customers}
                          getOptionLabel={(option) =>
                            `${option.Unvan}${
                              option.Yetkili ? ` - ${option.Yetkili}` : ""
                            }`
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.Id === value.Id
                          }
                          filterOptions={(x) => x}
                          loading={isCustomerLoading}
                          noOptionsText={
                            customerSearchMessage || "Sonuç bulunamadı"
                          }
                          disabled={fieldsDisabled}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Müşteri Seç"
                              required
                              disabled={fieldsDisabled}
                              helperText={
                                fieldsDisabled
                                  ? "Düzenleme modunda müşteri değiştirilemez"
                                  : customerSearchMessage
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <Search />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                endAdornment: (
                                  <>
                                    {isCustomerLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </CardContent>
                  </Card>

                  <Card elevation={0} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <SectionHeader icon={DescriptionIcon} title="Teklif Detayları" />
                      <Divider sx={{ mb: 2, mt: 0.5 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Açıklama"
                            name="Aciklama"
                            value={formValues.Aciklama}
                            onChange={handleFormChange}
                            fullWidth
                            multiline
                            rows={3}
                            disabled={fieldsDisabled}
                            helperText={
                              fieldsDisabled
                                ? "Düzenleme modunda açıklama değiştirilemez"
                                : ""
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                  <DescriptionIcon color="action" fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card elevation={0} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <SectionHeader icon={CalendarIcon} title="Koruma Tarihleri" />
                      <Divider sx={{ mb: 2, mt: 0.5 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Koruma Başlangıç"
                            name="KorumaBaslangic"
                            type="date"
                            value={formValues.KorumaBaslangic}
                            onChange={(e) => {
                              const selectedDate = e.target.value;
                              const today = new Date().toISOString().split("T")[0];

                              if (selectedDate > today) {
                                e.target.setCustomValidity(
                                  "Bugünden sonraki tarih seçilemez"
                                );
                              } else {
                                e.target.setCustomValidity("");
                                if (fieldsDisabled) {
                                  handleFormChange(e);
                                } else {
                                  handleStartDateChange(e);
                                }
                              }
                              e.target.reportValidity();
                            }}
                            fullWidth
                            disabled={fieldsDisabled}
                            InputLabelProps={{ shrink: true }}
                            helperText={
                              fieldsDisabled
                                ? "Düzenleme modunda tarihler değiştirilemez"
                                : ""
                            }
                            InputProps={{
                              inputProps: {
                                max: new Date().toISOString().split("T")[0],
                              },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon color="action" fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            error={false}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Koruma Bitiş"
                            name="KorumaBitis"
                            type="date"
                            value={formValues.KorumaBitis}
                            fullWidth
                            disabled={true}
                            InputLabelProps={{ shrink: true }}
                            helperText="Otomatik hesaplanır (60 gün)"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon color="action" fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {isEditMode && (
                    <Card elevation={0}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <SectionHeader icon={AssignmentIcon} title="Durum Bilgisi" />
                        <Divider sx={{ mb: 2, mt: 0.5 }} />
                        <FormControl fullWidth>
                          {(() => {
                            const onaylandiDurumId = statusOptions.find(
                              (s) => String(s.DurumAdi || "").toLowerCase().includes("onaylandı")
                            )?.Id;
                            const isDurumDisabled =
                              onaylandiDurumId != null &&
                              Number(formValues.DurumId) === Number(onaylandiDurumId);
                            return (
                          <Autocomplete
                            disabled={isDurumDisabled}
                            value={
                              statusOptions.find(
                                (s) => s.Id === parseInt(formValues.DurumId)
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              if (isDurumDisabled) return;
                              setFormValues((prev) => ({
                                ...prev,
                                DurumId: newValue?.Id || "",
                              }));
                            }}
                            options={statusOptions.filter((s) => {
                              const adi = (s.DurumAdi || "").toLowerCase();
                              return adi.includes("onayda bekliyor") || adi.includes("onaylandı") || adi.includes("red edildi");
                            })}
                            getOptionLabel={(option) => option.DurumAdi || ""}
                            isOptionEqualToValue={(option, value) =>
                              option.Id === value.Id
                            }
                            loading={isStatusLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Durum"
                                required
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <AssignmentIcon color="action" fontSize="small" />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  endAdornment: (
                                    <>
                                      {isStatusLoading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                            );
                          })()}
                        </FormControl>
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })()}
          </Box>
        </Box>
        </ThemeProvider>
      </Drawer>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <DialogTitle>Müşteri Ara</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Unvan, Yetkili, Vergi No veya Telefon ile ara"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            margin="normal"
            placeholder="En az 3 karakter giriniz..."
            autoFocus
          />
          <Box sx={{ mt: 2 }}>
            {isSearching ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            ) : searchResults.length > 0 ? (
              searchResults.map((customer) => (
                <Button
                  key={customer.Id}
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setFormValues((prev) => ({
                      ...prev,
                      MusteriId: customer.Id,
                    }));
                    setDialogOpen(false);
                    setSearchQuery("");
                  }}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    textTransform: "none",
                    p: 2,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box textAlign="left">
                    <Box fontWeight="bold">{customer.Unvan}</Box>
                    <Box
                      display="flex"
                      gap={2}
                      color="text.secondary"
                      fontSize="0.875rem"
                    >
                      <span>{customer.Yetkili}</span>
                      {customer.VergiNo && <span>VN: {customer.VergiNo}</span>}
                      {customer.Tel && <span>{customer.Tel}</span>}
                    </Box>
                  </Box>
                </Button>
              ))
            ) : searchQuery.length > 1 ? (
              <Box p={2} textAlign="center" color="text.secondary">
                Sonuç bulunamadı
              </Box>
            ) : (
              <Box p={2} textAlign="center" color="text.secondary">
                Arama yapmak için en az 3 karakter giriniz
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teklif Görüntüleme Dialog - sadece teklif koruma; her bayi kendi teklifini görür, Bayi 1 hepsini */}
      <Dialog
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setViewOffer(null); }}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssignmentIcon color="action" />
          Teklif Detayı
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : viewOffer ? (
            <Box sx={{ pt: 0.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Teklif No</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewOffer.TeklifNo ?? "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Bayi</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewOffer.BayiAdi ?? viewOffer.Bayi?.BayiAdi ?? "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Müşteri</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewOffer.MusteriUnvan ?? viewOffer.Musteri?.Unvan ?? viewOffer.PotansiyelMusteri?.Unvan ?? "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Koruma Başlangıç</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewOffer.KorumaBaslangic
                      ? new Date(viewOffer.KorumaBaslangic).toLocaleDateString("tr-TR")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Koruma Bitiş</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewOffer.KorumaBitis
                      ? new Date(viewOffer.KorumaBitis).toLocaleDateString("tr-TR")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Durum</Typography>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{
                      color: (() => {
                        const durumAdi = (viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi || "").toString();
                        if (durumAdi.toLowerCase().includes("red edildi")) return "#d32f2f";
                        if (durumAdi.toLowerCase().includes("onaylandı")) return "success.main";
                        return "text.primary";
                      })(),
                    }}
                  >
                    {(viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi)
                      ? (String(viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi).toLowerCase().includes("onaylandı")
                          ? "Onaylandı"
                          : String(viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi).toLowerCase().includes("red edildi")
                            ? "Red Edildi"
                            : viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi)
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Kayıt Tarihi</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {viewOffer.KayitTarihi
                      ? new Date(viewOffer.KayitTarihi).toLocaleDateString("tr-TR")
                      : "—"}
                  </Typography>
                </Grid>
                {viewOffer.Aciklama && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Açıklama</Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: "pre-wrap" }}>
                      {viewOffer.Aciklama}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Teklif içeriği: paket, modüller, kullanıcı sayısı, fiyat özeti (HesaplamaSonucu) */}
              {(() => {
                let hesaplama = null;
                try {
                  if (viewOffer.HesaplamaSonucu) {
                    hesaplama = typeof viewOffer.HesaplamaSonucu === "string"
                      ? JSON.parse(viewOffer.HesaplamaSonucu)
                      : viewOffer.HesaplamaSonucu;
                  }
                } catch (_) {}
                if (!hesaplama) return null;
                const detay = hesaplama.hesaplamaDetaylari || {};
                const formatTL = (n) =>
                  Number(n) != null && !Number.isNaN(Number(n))
                    ? new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }).format(Number(n))
                    : "—";
                const modulSatirlari = detay.modulSatirlari || [];
                const toplamFiyat = hesaplama.toplamFiyat ?? detay.toplam ?? 0;
                const kullaniciSayisiRaw = hesaplama.KullaniciSayisi ?? hesaplama.kullaniciSayisi;
                const kullaniciSayisi = kullaniciSayisiRaw != null && kullaniciSayisiRaw !== "" ? Number(kullaniciSayisiRaw) : null;
                const paketAdi = hesaplama.paket?.Adi ?? null;
                const bulutVar = (detay.bulutSunucuFiyati ?? 0) > 0 || (detay.bulutSunucuEkKullaniciFiyati ?? 0) > 0;
                const onlineBankaVar = (detay.onlineBankaFiyati ?? 0) > 0;
                const onlineBankaAdetRaw = hesaplama.OnlineBankaPaket ?? hesaplama.onlineBankaPaket;
                const onlineBankaAdet = onlineBankaAdetRaw != null && onlineBankaAdetRaw !== "" ? Number(onlineBankaAdetRaw) : null;

                return (
                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <InventoryIcon fontSize="small" />
                      Teklif İçeriği
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Teklif Tipi</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewOffer.TeklifTipi === "yillikYenileme" || viewOffer.TeklifTipi === "Yıllık Yenileme" ? "Yıllık Yenileme" : "Yeni Lisans"}
                        </Typography>
                      </Grid>
                      {paketAdi && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Paket</Typography>
                          <Typography variant="body2" fontWeight={500}>{paketAdi}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Kullanıcı Sayısı</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {kullaniciSayisi != null && !Number.isNaN(kullaniciSayisi) ? kullaniciSayisi : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Banka Sayısı (Online Banka)</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {onlineBankaAdet != null && !Number.isNaN(onlineBankaAdet) && onlineBankaAdet > 0
                            ? `${onlineBankaAdet} banka`
                            : onlineBankaVar
                              ? "Evet (adet kayıtlı değil)"
                              : "—"}
                        </Typography>
                      </Grid>
                      {modulSatirlari.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Modüller</Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5, py: 0.5 }}>
                            {modulSatirlari.map((s, i) => (
                              <li key={i}>
                                <Typography variant="body2" component="span">
                                  {s.ProgramAdi ?? s.ModulAdi ?? "Modül"}
                                  {s.Fiyat != null && Number(s.Fiyat) > 0 && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                      — {formatTL(s.Fiyat)}
                                    </Typography>
                                  )}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Bulut Sunucu</Typography>
                        <Typography variant="body2" fontWeight={500}>{bulutVar ? "Evet" : "Hayır"}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Online Banka</Typography>
                        <Typography variant="body2" fontWeight={500}>{onlineBankaVar ? "Evet" : "Hayır"}</Typography>
                      </Grid>

                      {/* Alt kısım: fiyatlar yan yana */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Paket Fiyatı</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatTL(detay.paketFiyati ?? detay.paketFiyatiOrijinal ?? 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Modül Fiyatları Toplam</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatTL((detay.modulFiyatlari ?? 0) + (detay.eFaturaEIrsaliyeFiyati ?? 0))}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Bulut Sunucu (fiyat)</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {bulutVar ? formatTL(detay.bulutSunucuFiyati ?? 0) : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Online Banka (fiyat)</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {onlineBankaVar ? formatTL(detay.onlineBankaFiyati ?? 0) : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Bulut Ek Kullanıcı Fiyatı</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatTL(detay.bulutSunucuEkKullaniciFiyati ?? 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Paket Ek Kullanıcı Fiyatı</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatTL(detay.ekKullaniciFiyati ?? detay.ekKullaniciFiyatiOrijinal ?? 0)}
                        </Typography>
                      </Grid>
                      {(detay.toplamIskontoTutari ?? 0) > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Toplam İndirim</Typography>
                          <Typography variant="body2" fontWeight={600} color="success.dark">-{formatTL(detay.toplamIskontoTutari)}</Typography>
                        </Grid>
                      )}
                      {(() => {
                        const ekstra = hesaplama.ekstraSatirlar || [];
                        const ekstraFiltre = ekstra.filter(
                          (s) => s.aciklama && ((Number(s.birimFiyat) || 0) >= 0 || (Number(s.miktar) || 0) > 0)
                        );
                        const ekstraToplam = ekstraFiltre.reduce(
                          (acc, s) =>
                            acc +
                            (Number(s.miktar) || 0) *
                              (Number(s.birimFiyat) || 0) *
                              (1 - Math.max(0, Math.min(100, Number(s.iskontoOrani) || 0)) / 100),
                          0
                        );
                        if (ekstraFiltre.length === 0) return null;
                        return (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Ek Satırlar</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, py: 0.5 }}>
                                {ekstraFiltre.map((s, i) => {
                                  const m = Number(s.miktar) || 1;
                                  const f = Number(s.birimFiyat) || 0;
                                  const isk = Math.max(0, Math.min(100, Number(s.iskontoOrani) || 0));
                                  const tutar = m * f * (1 - isk / 100);
                                  return (
                                    <li key={i}>
                                      <Typography variant="body2" component="span">
                                        {String(s.aciklama || "").trim() || "—"}
                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                          — {m} adet × {formatTL(f)}
                                          {isk > 0 && ` (İsk. %${isk})`} = {formatTL(tutar)}
                                        </Typography>
                                      </Typography>
                                    </li>
                                  );
                                })}
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Ek Satırlar Toplam</Typography>
                              <Typography variant="body2" fontWeight={600}>{formatTL(parseFloat(ekstraToplam.toFixed(2)))}</Typography>
                            </Grid>
                          </>
                        );
                      })()}
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Toplam Fiyat</Typography>
                        <Typography variant="body1" fontWeight={700} color="primary">{formatTL(toplamFiyat)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })()}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button onClick={() => { setViewModalOpen(false); setViewOffer(null); }} color="primary">
            Kapat
          </Button>
          {viewOffer && (
            <>
              <Button
                variant="contained"
                startIcon={printLoading ? <CircularProgress size={18} color="inherit" /> : <FaPrint />}
                onClick={() => handlePrintOffer(viewOffer.Id)}
                disabled={printLoading}
              >
                Yazdır
              </Button>
              {(() => {
                const durumAdi = (viewOffer.DurumAdi || viewOffer.Durum?.DurumAdi || "").toString();
                const onayliByAdi = durumAdi.toLowerCase().includes("onaylandı");
                const onayliDurumId = statusOptions.find((s) => String(s.DurumAdi || "").toLowerCase().includes("onaylandı"))?.Id;
                const onayliById = onayliDurumId != null && Number(viewOffer.DurumId) === Number(onayliDurumId);
                if (onayliByAdi || onayliById) return null;
                return (
                  <Button variant="contained" color="success" onClick={handleOpenOnayla}>
                    Siparişe Dönüştür
                  </Button>
                );
              })()}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Onayla modal: müşteri bilgileri + onaylı teklif dökümanı */}
      <Dialog
        open={onaylaModalOpen}
        onClose={() => setOnaylaModalOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Teklifi Siparişe Dönüştür</DialogTitle>
        <DialogContent dividers>
          {onaylaFiles.length === 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: "warning.light", color: "warning.contrastText", borderRadius: 1 }}>
              <Typography variant="body2">En az bir PDF dosyası yükleyin. PDF olmadan siparişe dönüştürülemez.</Typography>
            </Box>
          )}
          {onaylaMissingFields.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: "error.light", color: "error.contrastText", borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600}>Eksik alanlar: </Typography>
              <Typography variant="body2">{onaylaMissingFields.join(", ")}</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            {ONAYLA_FORM_FIELDS.map(({ field, label, sm, required, numeric }) => {
              const showError = onaylaAttempted && Boolean(getOnaylaFieldError(field, onaylaCustomer));
              return (
                <Grid item xs={12} sm={sm} key={field}>
                  <TextField
                    fullWidth
                    size="small"
                    name={field}
                    id={`onayla-customer-${field}`}
                    autoComplete="off"
                    required={required}
                    label={label}
                    value={onaylaCustomer[field] ?? ""}
                    error={showError}
                    helperText={showError ? getOnaylaFieldError(field, onaylaCustomer) : undefined}
                    inputProps={
                      numeric
                        ? {
                            maxLength: numeric,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }
                        : undefined
                    }
                    onChange={(e) => {
                      const nextValue = normalizeOnaylaFieldValue(field, e.target.value);
                      setOnaylaCustomer((prev) => {
                        const next = { ...prev, [field]: nextValue };
                        if (onaylaAttempted) {
                          setOnaylaMissingFields(getOnaylaCariEksikler(next));
                        }
                        return next;
                      });
                    }}
                  />
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <div className="po-file-upload">
                <span className="po-file-upload-label">Onaylı teklif dökümanı (PDF)</span>
                <div
                  className={`po-file-upload-zone${onaylaFiles.length > 0 ? " has-files" : ""}${onaylaFileDragOver ? " drag-over" : ""}`}
                  onClick={() => {
                    if (onaylaFiles.length === 0) onaylaFileInputRef.current?.click();
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setOnaylaFileDragOver(true);
                  }}
                  onDragLeave={() => setOnaylaFileDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOnaylaFileDragOver(false);
                    addOnaylaPdfFiles(e.dataTransfer.files);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onaylaFileInputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    ref={onaylaFileInputRef}
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={(e) => {
                      addOnaylaPdfFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  {onaylaFiles.length === 0 ? (
                    <>
                      <span className="po-file-upload-icon">
                        <CloudUploadIcon fontSize="small" />
                      </span>
                      <span className="po-file-upload-title">PDF dosyası yükleyin</span>
                      <span className="po-file-upload-hint">Sürükleyip bırakın veya dosya seçin</span>
                      <span className="po-file-upload-action">
                        <CloudUploadIcon sx={{ fontSize: 18 }} />
                        Dosya Seç
                      </span>
                    </>
                  ) : (
                    <div className="po-file-list" onClick={(e) => e.stopPropagation()}>
                      {onaylaFiles.map((file, index) => (
                        <div className="po-file-item" key={`${file.name}-${index}`}>
                          <div className="po-file-item-info">
                            <span className="po-file-item-icon">
                              <PictureAsPdfIcon fontSize="small" />
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <div className="po-file-item-name" title={file.name}>
                                {file.name}
                              </div>
                              <div className="po-file-item-size">{formatOnaylaFileSize(file.size)}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="po-file-item-remove"
                            aria-label="Dosyayı kaldır"
                            onClick={() => setOnaylaFiles((prev) => prev.filter((_, i) => i !== index))}
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="po-file-upload-add"
                        onClick={() => onaylaFileInputRef.current?.click()}
                      >
                        <CloudUploadIcon sx={{ fontSize: 16 }} />
                        Başka dosya ekle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnaylaModalOpen(false)}>İptal</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleOnaylaSubmit}
            disabled={onaylaLoading || onaylaFiles.length === 0 || !isOnaylaCariComplete(onaylaCustomer)}
          >
            {onaylaLoading ? <CircularProgress size={22} color="inherit" /> : "Siparişe Dönüştür"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* format=html: Tam HTML gizli iframe'de – yazdırma iframe'den, içerik + stil aynen */}
      {printFullHtml && (
        <iframe
          ref={printIframeRef}
          srcDoc={printFullHtml}
          title="Teklif yazdır"
          style={{ position: "absolute", left: "-9999px", width: "21cm", minHeight: "29.7cm", border: 0 }}
          onLoad={handlePrintIframeLoad}
        />
      )}
      {/* Eski div tabanlı yazdırma (gerekirse fallback) */}
      {printBodyHtml && (
        <>
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * { visibility: hidden; }
              #__print_root_offer__, #__print_root_offer__ * { visibility: visible; }
              #__print_root_offer__ { position: absolute; left: 0; top: 0; width: 100%; }
            }
          `}} />
          <div
            id="__print_root_offer__"
            style={{ position: "absolute", left: "-9999px", top: 0 }}
            dangerouslySetInnerHTML={{ __html: printBodyHtml }}
          />
          {printCssText && (
            <style id="__print_css_offer__" dangerouslySetInnerHTML={{ __html: printCssText }} />
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Teklif Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography>
            Bu teklifi silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            İptal
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>


    </div>
  );
};

export default Offer;
